import {
  useNavigate,
  createSearchParams,
  generatePath,
} from 'react-router-dom';
import { AppRoutes } from 'routes/routeConstants/appRoutes';
import { removeFalsyKeys } from './removeFalsyKeys';

const useFarmboxRedirections = () => {
  const navigate = useNavigate();

  const redirect = (props: {
    pathname: string;
    queryParams?: {};
    hash?: string;
    replace?: boolean;
    state?: string;
  }) => {
    const { pathname, queryParams, hash, replace, state } = props;
    navigate(
      {
        pathname,
        search:
          queryParams &&
          createSearchParams(removeFalsyKeys(queryParams)).toString(),
        hash,
      },
      { replace, state }
    );
  };

  const redirectToFarm = (
    currentTab: string,
    params?: { page?: number; limit?: number }
  ) =>
    redirect({
      pathname: `/farm/${currentTab}`,
      queryParams: params,
    });

  const redirectToRespectiveCommodityStage = (
    id?: number,
    stage?: string,
    status?: string,
    params?: { isFrom?: string; page?: number }
  ) =>
    redirect({
      pathname: generatePath(
        `/${AppRoutes.FARM_CREATION}/${stage}/${id}/${status}`,
        {
          id,
          status,
        }
      ),
      queryParams: params,
    });

  const redirectToFarmFuelAndEnergy = (
    id?: number,
    status?: string,
    params?: { isFrom?: string; page?: number }
  ) =>
    redirect({
      pathname: generatePath(
        `/${AppRoutes.FARM_CREATION}/fuel-and-energy/${id}/${status}`,
        {
          id,
          status,
        }
      ),
      queryParams: params,
    });

  const redirectToCommodityDraft = (replace?: boolean) =>
    redirect({
      pathname: generatePath(`/${AppRoutes.FARMBOX}/draft`),
    });

  const redirectToFarmField = (
    id?: number,
    status?: string,
    params?: { isFrom?: string; page?: number }
  ) =>
    redirect({
      pathname: generatePath(
        `/${AppRoutes.FARM_CREATION}/field/${id}/${status}`,
        {
          id,
          status,
        }
      ),
      queryParams: params,
    });

  const redirectToCommodityOverview = (
    id: number,
    status?: string,
    params?: { isFrom?: string; page?: number }
  ) =>
    redirect({
      pathname: generatePath(AppRoutes.FARM_OVERVIEW_PAGE, { id, status }),
      queryParams: params,
    });

  const redirectToFarmCommodityDistribution = (
    id?: number,
    status?: string,
    params?: { isFrom?: string; page?: number }
  ) =>
    redirect({
      pathname: generatePath(
        `/${AppRoutes.FARM_CREATION}/commodity-distribution/${id}/${status}`,
        {
          id,
          status,
        }
      ),
      queryParams: params,
    });

  const redirectToFarmRawMaterial = (
    id?: number,
    status?: string,
    params?: { isFrom?: string; page?: number },
    replace?: boolean
  ) =>
    redirect({
      pathname: generatePath(
        `/${AppRoutes.FARM_CREATION}/raw-material/${id}/${status}`,
        {
          id,
          status,
        }
      ),
      queryParams: params,
      replace,
    });

  const redirectToFarmTransportation = (
    id?: number,
    status?: string,
    params?: { isFrom?: string; page?: number },
    replace?: boolean
  ) =>
    redirect({
      pathname: generatePath(
        `/${AppRoutes.FARM_CREATION}/transportation/${id}/${status}`,
        {
          id,
          status,
        }
      ),
      queryParams: params,
      replace,
    });
  const redirectToLiveStockGrowthStage = (
    id?: number,
    status?: string,
    params?: { isFrom?: string; page?: number }
  ) =>
    redirect({
      pathname: generatePath(`/${AppRoutes.FARM_LIVE_STOCK_GROWTH_STAGE}`, {
        id,
        status,
      }),
      queryParams: params,
    });
  const redirectToLiveStockFeed = (
    id?: number,
    status?: string,
    params?: { isFrom?: string; page?: number }
  ) =>
    redirect({
      pathname: generatePath(`/${AppRoutes.FARM_LIVE_STOCK_FEED}`, {
        id,
        status,
      }),
      queryParams: params,
    });
  const redirectToLiveStockFuelAndEnergy = (
    id?: number,
    status?: string,
    params?: { isFrom?: string; page?: number }
  ) =>
    redirect({
      pathname: generatePath(`/${AppRoutes.FARM_LIVE_STOCK_FUEL_AND_ENERGY}`, {
        id,
        status,
      }),
      queryParams: params,
    });

  const redirectToLiveStockManureManagement = (
    id: number,
    status?: string,
    params?: { isFrom?: string; page?: number }
  ) =>
    redirect({
      pathname: generatePath(AppRoutes.FARM_LIVE_STOCK_MANURE_MANAGEMENT, {
        id,
        status,
      }),
      queryParams: params,
    });

  const redirectToLiveStockCommodityDistribution = (
    id: number,
    status?: string,
    params?: { isFrom?: string; page?: number }
  ) =>
    redirect({
      pathname: generatePath(AppRoutes.FARM_LIVE_STOCK_COMMODITY_DISTRIBUTION, {
        id,
        status,
      }),
      queryParams: params,
    });

  const redirectToLiveStockCommodityOverview = (
    id: number,
    status?: string,
    params?: { isFrom?: string; page?: number }
  ) =>
    redirect({
      pathname: generatePath(AppRoutes.FARM_LIVE_STOCK_OVERVIEW, {
        id,
        status,
      }),
      queryParams: params,
    });

  const redirectToRespectiveLiveStockCommodityStage = (
    id?: number,
    stage?: string,
    status?: string,
    params?: { isFrom?: string; page?: number }
  ) =>
    redirect({
      pathname: generatePath(
        `/${AppRoutes.FARM_LIVE_STOCK_CREATION}/${stage}/${id}/${status}`,
        {
          id,
          status,
        }
      ),
      queryParams: params,
    });

  return {
    redirectToFarm,
    redirectToRespectiveCommodityStage,
    redirectToFarmFuelAndEnergy,
    redirectToCommodityDraft,
    redirectToFarmField,
    redirectToCommodityOverview,
    redirectToFarmCommodityDistribution,
    redirectToFarmRawMaterial,
    redirectToFarmTransportation,
    redirectToLiveStockGrowthStage,
    redirectToLiveStockFeed,
    redirectToLiveStockFuelAndEnergy,
    redirectToLiveStockManureManagement,
    redirectToLiveStockCommodityDistribution,
    redirectToLiveStockCommodityOverview,
    redirectToRespectiveLiveStockCommodityStage
  };
};

export default useFarmboxRedirections;
